/**
 * Chat contracts shared by the composer, the transcript and the page container.
 *
 * `OutgoingMessage` is the important one. The composer used to call
 * `onSend()` with no arguments at all, and the "внутренняя заметка" toggle only
 * changed the input's colours — so a note a manager wrote about a customer was
 * delivered to that customer. A discriminated union with no default makes that
 * mistake impossible to express: every caller has to say which kind of message
 * it is building, and the backend requires the same `kind` field.
 */

export type OutgoingMessageKind = 'reply' | 'note';

export type OutgoingMessage =
  /** Delivered to the customer through the channel. */
  | { kind: 'reply'; text: string }
  /** Team-only note. Stored on the dialog, never delivered. */
  | { kind: 'note'; text: string };

export interface ChatMessage {
  id: number;
  conversationId?: number;
  direction: 'in' | 'out' | string;
  author: 'client' | 'manager' | 'ai' | string;
  text: string;
  /** True for a team note. Set by the server; the transcript renders it apart. */
  internal?: boolean;
  mediaUrl?: string | null;
  mediaType?: 'image' | 'audio' | 'video' | 'document' | string | null;
  mediaName?: string | null;
  mediaSize?: number | null;
  deliveryStatus?: string;
  deliveryError?: string | null;
  sentAt?: string;
  /** Optimistic row that exists only in this browser tab. */
  clientOnly?: boolean;
}

/** A message is a note only when the server says so. */
export const isInternalNote = (message: Pick<ChatMessage, 'internal'>): boolean => message.internal === true;

/**
 * ─── AI-выжимка: the single call site ───────────────────────────────────────
 *
 * The sidebar used to show one hardcoded paragraph for every customer. The real
 * digest is built by the AI workstream's `ConversationSummaryService`; this is
 * the only place the URL appears, so pointing at the final route is a one-line
 * change here and nothing else moves.
 */
export const AI_SUMMARY_ENDPOINT = (conversationId: number): string =>
  `/ai/conversations/${conversationId}/summary`;

export interface ConversationSummary {
  /** One factual line. */
  headline: string;
  /** Factual lines, ready to render as a list. */
  bullets: string[];
  /** Shown verbatim next to the digest: says how it was produced. */
  disclaimer: string;
  /** 'rules' — counted from the dialog, 'llm' — written by a model. */
  source: 'rules' | 'llm' | string;
}

/**
 * Accept the digest shape defensively: whichever of `headline`/`summary`/`text`
 * the endpoint settles on, the panel renders something true or shows its empty
 * state — it never falls back to invented text.
 */
export function normalizeSummary(payload: any): ConversationSummary | null {
  if (!payload || typeof payload !== 'object') return null;
  const headline = String(payload.headline || payload.summary || payload.text || '').trim();
  const bullets = Array.isArray(payload.bullets)
    ? payload.bullets.map((item: unknown) => String(item || '').trim()).filter(Boolean)
    : [];
  if (!headline && bullets.length === 0) return null;
  return {
    headline,
    bullets,
    disclaimer: String(payload.disclaimer || '').trim(),
    source: payload.source === 'llm' ? 'llm' : 'rules',
  };
}

export function formatMediaSize(bytes: number | null | undefined): string {
  const value = Number(bytes || 0);
  if (value <= 0) return '';
  if (value < 1024) return `${value} Б`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`;
  return `${(value / (1024 * 1024)).toFixed(1)} МБ`;
}
